import { alpha, useTheme } from '@mui/material';
import AlignHorizontalLeftIcon from '@mui/icons-material/AlignHorizontalLeft';
import AlignHorizontalRightIcon from '@mui/icons-material/AlignHorizontalRight';
import DSBox from '../../../design-system/DSBox';
import DSTextField from '../../../design-system/DSTextField';
import { DSTypography } from '../../../design-system/Typography';
import { KLYDO_BLACK, KLYDO_YELLOW } from '../../../consts/colors';
import MediaUploadZone from './MediaUploadZone';
import useSectionMediaUpload from '../hooks/useSectionMediaUpload';
import { Section } from '../types';
import { getScaledCloudinaryUrl } from '../../../utils';
import {
  ArtistProfileTemplate,
  ArtistProfileTemplateField,
  SectionFieldType,
} from '../../../api/public';

const getTextareaScrollbarSx = (scrollbarWidth: string) => ({
  '& textarea': {
    '&::-webkit-scrollbar': { width: scrollbarWidth },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'action.selected',
      borderRadius: '64px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
  },
});

type LayoutToggleProps = {
  layout: 'left' | 'right';
  onToggle: (layout: 'left' | 'right') => void;
  leftLabel: string;
  rightLabel: string;
};

const LayoutToggle = ({
  layout,
  onToggle,
  leftLabel,
  rightLabel,
}: LayoutToggleProps) => {
  const optionSx = (selected: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    px: selected ? '20px' : '12px',
    py: selected ? '10px' : '8px',
    border: 'none',
    borderRadius: '1000px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    ...(selected
      ? {
          backgroundColor: KLYDO_YELLOW,
          color: KLYDO_BLACK,
          boxShadow: 2,
        }
      : {
          backgroundColor: 'transparent',
          color: 'action.disabled',
        }),
  });

  const textSx = (selected: boolean) => ({
    fontFamily: 'Gilroy-Bold',
    fontSize: selected ? '16px' : '13px',
    lineHeight: '22px',
    letterSpacing: '0.46px',
    color: 'inherit',
  });

  return (
    <DSBox
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        backgroundColor: 'secondary.main',
        borderRadius: '1000px',
        p: '5px',
        width: 'fit-content',
      }}
    >
      <DSBox
        component="button"
        onClick={() => onToggle('left')}
        sx={optionSx(layout === 'left')}
      >
        <AlignHorizontalLeftIcon
          sx={{ fontSize: layout === 'left' ? 20 : 16 }}
        />
        <DSTypography sx={textSx(layout === 'left')}>{leftLabel}</DSTypography>
      </DSBox>
      <DSBox
        component="button"
        onClick={() => onToggle('right')}
        sx={optionSx(layout === 'right')}
      >
        <AlignHorizontalRightIcon
          sx={{ fontSize: layout === 'right' ? 20 : 16 }}
        />
        <DSTypography sx={textSx(layout === 'right')}>
          {rightLabel}
        </DSTypography>
      </DSBox>
    </DSBox>
  );
};

type DynamicFieldProps = {
  field: ArtistProfileTemplateField;
  value: string;
  onValueChange: (value: string) => void;
  readOnly?: boolean;
  fullWidth?: boolean;
  stretchTextarea?: boolean;
  onUploadStart?: () => void;
  onUploadEnd?: () => void;
};

const DynamicField = ({
  field,
  value,
  onValueChange,
  readOnly,
  fullWidth,
  stretchTextarea,
  onUploadStart,
  onUploadEnd,
}: DynamicFieldProps) => {
  const { spacing, palette } = useTheme();
  const { handleFileSelected, isUploading } = useSectionMediaUpload({
    onUploaded: onValueChange,
    onUploadStart,
    onUploadEnd,
  });

  const sectionFieldSx = {
    borderRadius: spacing(1),
    background: alpha(palette.common.white, 0.08),
    '& .MuiInputBase-root': {
      padding: spacing(1, 1.5),
    },
    '& .MuiInputBase-input, & textarea': {
      overflowY: 'auto' as const,
      padding: 0,
    },
  };

  // In readOnly mode, render plain text instead of form inputs
  if (readOnly) {
    switch (field.type) {
      case SectionFieldType.IMAGE:
        return value ? (
          <img
            src={getScaledCloudinaryUrl(value, fullWidth ? 1600 : 800)}
            alt={field.label}
            style={{
              width: '100%',
              maxHeight: fullWidth ? '700px' : undefined,
              objectFit: 'cover',
              borderRadius: spacing(3),
            }}
          />
        ) : null;
      case SectionFieldType.VIDEO:
        return value ? (
          <MediaUploadZone
            label={field.label}
            mediaUrl={value}
            mediaType={SectionFieldType.VIDEO}
            readOnly
            maxHeight={fullWidth ? '500px' : undefined}
          />
        ) : null;
      case SectionFieldType.TEXT:
        return value ? (
          <DSTypography variant="h5" color="common.white">
            {value}
          </DSTypography>
        ) : null;
      case SectionFieldType.TEXTAREA:
        return value ? (
          <DSTypography
            sx={{
              fontFamily: 'Gilroy-Medium',
              fontSize: '16px',
              lineHeight: 1.5,
              color: 'common.white',
            }}
          >
            {value}
          </DSTypography>
        ) : null;
      case SectionFieldType.SELECT:
        return null;
      default:
        return null;
    }
  }

  // Edit mode — render form inputs
  switch (field.type) {
    case SectionFieldType.IMAGE:
      return (
        <DSBox sx={{ height: '100%' }}>
          <MediaUploadZone
            label={field.label}
            subtitle={'PNG, JPG, JPEG — Max 10MB'}
            mediaUrl={value}
            isLoading={isUploading}
            onFileSelected={handleFileSelected}
            accept="image/jpeg,image/png,image/jpg"
            minHeight={fullWidth ? spacing(50) : spacing(35)}
            {...(fullWidth && {
              maxHeight: spacing(60),
            })}
          />
        </DSBox>
      );
    case SectionFieldType.VIDEO:
      return (
        <DSBox sx={{ height: '100%' }}>
          <MediaUploadZone
            label={field.label}
            subtitle={'16:9 | MP4 — Max 70MB'}
            mediaUrl={value}
            isLoading={isUploading}
            onFileSelected={handleFileSelected}
            mediaType={SectionFieldType.VIDEO}
            accept="video/mp4"
            {...(fullWidth
              ? {
                  minHeight: spacing(50),
                  maxHeight: spacing(80),
                }
              : {
                  maxHeight: spacing(80),
                })}
          />
        </DSBox>
      );
    case SectionFieldType.TEXTAREA:
      return (
        <DSTextField
          placeholder={field.label}
          fullWidth
          multiline
          minRows={stretchTextarea ? 10 : 5}
          sx={{
            ...getTextareaScrollbarSx(spacing(1)),
            ...(stretchTextarea && { flex: 1 }),
            borderRadius: spacing(1),
            background: alpha(palette.common.white, 0.08),
            padding: 0,
            '& .MuiOutlinedInput-root': {
              padding: spacing(1.5, 2),
              height: '100%',
              alignItems: 'flex-start',
            },
            '& .MuiInputBase-input': {
              padding: 0,
            },
          }}
          value={value}
          onChange={(event) => onValueChange(event.target.value)}
        />
      );
    case SectionFieldType.SELECT:
      return (
        <LayoutToggle
          layout={value === 'left' || value === 'right' ? value : 'left'}
          onToggle={onValueChange}
          leftLabel={field.options?.[0] ?? 'Left'}
          rightLabel={field.options?.[1] ?? 'Right'}
        />
      );
    case SectionFieldType.TEXT:
    default:
      return (
        <DSTextField
          placeholder={field.label}
          fullWidth
          value={value}
          onChange={(event) => onValueChange(event.target.value)}
          sx={{
            ...sectionFieldSx,
            padding: 0,
            '& .MuiOutlinedInput-root': {
              padding: spacing(1.5, 2),
            },
            '& .MuiInputBase-input': {
              padding: 0,
            },
          }}
        />
      );
  }
};

type SectionTemplateDispatcherProps = {
  section: Section;
  onUpdate: (data: Record<string, string>) => void;
  templates: ArtistProfileTemplate[];
  readOnly?: boolean;
  onUploadStart?: () => void;
  onUploadEnd?: () => void;
};

const SectionTemplateDispatcher = ({
  section,
  onUpdate,
  templates,
  readOnly,
  onUploadStart,
  onUploadEnd,
}: SectionTemplateDispatcherProps) => {
  const { spacing } = useTheme();
  const template = templates.find(
    (profileTemplate) => profileTemplate.key === section.type,
  );

  if (!template) {
    return null;
  }

  const makeOnValueChange = (fieldKey: string) => (newValue: string) =>
    onUpdate({ [fieldKey]: newValue });

  // Separate grouped fields from ungrouped ones
  const groupedFields = template.fields.filter((field) => field.group);
  const ungroupedFields = template.fields.filter((field) => !field.group);

  // Collect unique groups in order of first appearance
  const groupOrder: string[] = [];
  for (const field of groupedFields) {
    if (field.group && !groupOrder.includes(field.group)) {
      groupOrder.push(field.group);
    }
  }

  const hasGroups = groupOrder.length > 1;

  // Determine layout direction from the layout select field (if any)
  const layoutField = ungroupedFields.find(
    (field) => field.type === SectionFieldType.SELECT,
  );
  const layoutValue = layoutField
    ? (section.data[layoutField.key] as 'left' | 'right') || 'left'
    : 'left';

  const hasVideoField = groupedFields.some(
    (field) => field.type === SectionFieldType.VIDEO,
  );

  const allGroupsAreMedia = groupOrder.every((groupName) =>
    groupedFields
      .filter((field) => field.group === groupName)
      .every(
        (field) =>
          field.type === SectionFieldType.IMAGE ||
          field.type === SectionFieldType.VIDEO,
      ),
  );

  if (hasGroups) {
    return (
      <DSBox
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: readOnly ? '40px' : spacing(2),
        }}
      >
        <DSBox
          sx={{
            display: 'flex',
            gap: readOnly ? '40px' : spacing(2),
            alignItems: readOnly ? 'flex-start' : 'stretch',
            flexDirection: layoutValue === 'right' ? 'row-reverse' : 'row',
            ...(!readOnly && { minHeight: spacing(22) }),
          }}
        >
          {groupOrder.map((groupName) => {
            const fieldsInGroup = groupedFields.filter(
              (field) => field.group === groupName,
            );
            const isMediaGroup = fieldsInGroup.every(
              (field) =>
                field.type === SectionFieldType.IMAGE ||
                field.type === SectionFieldType.VIDEO,
            );
            return (
              <DSBox
                key={groupName}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: readOnly ? (isMediaGroup ? 0 : '16px') : spacing(2),
                  ...(readOnly &&
                    isMediaGroup &&
                    !allGroupsAreMedia && {
                      flex: '0 0 45%',
                      '& img, & video': {
                        aspectRatio: '3/2',
                        objectFit: 'cover',
                        borderRadius: spacing(3),
                      },
                    }),
                  ...(readOnly &&
                    allGroupsAreMedia && {
                      '& img': { aspectRatio: '1/1' },
                    }),
                  ...(!readOnly &&
                    isMediaGroup &&
                    !allGroupsAreMedia && {
                      flex: '0 0 45%',
                      '& > *': { height: '100%' },
                      '& label': {
                        height: '100%',
                        minHeight: 'unset !important',
                      },
                    }),
                  ...(!readOnly &&
                    allGroupsAreMedia && {
                      '& > *': { flex: 1 },
                      '& > * > *': { height: spacing(60) },
                    }),
                }}
              >
                {fieldsInGroup.map((field) => (
                  <DynamicField
                    key={field.key}
                    field={field}
                    value={section.data[field.key] ?? ''}
                    onValueChange={makeOnValueChange(field.key)}
                    readOnly={readOnly}
                    stretchTextarea={hasVideoField}
                    onUploadStart={onUploadStart}
                    onUploadEnd={onUploadEnd}
                  />
                ))}
              </DSBox>
            );
          })}
        </DSBox>
        {ungroupedFields
          .filter(
            (field) => !(readOnly && field.type === SectionFieldType.SELECT),
          )
          .map((field) => (
            <DynamicField
              key={field.key}
              field={field}
              value={section.data[field.key] ?? ''}
              onValueChange={makeOnValueChange(field.key)}
              readOnly={readOnly}
              onUploadStart={onUploadStart}
              onUploadEnd={onUploadEnd}
            />
          ))}
      </DSBox>
    );
  }

  // No groups: all fields stacked vertically
  return (
    <DSBox sx={{ display: 'flex', flexDirection: 'column', gap: spacing(2) }}>
      {template.fields
        .filter(
          (field) => !(readOnly && field.type === SectionFieldType.SELECT),
        )
        .map((field) => (
          <DynamicField
            key={field.key}
            field={field}
            value={section.data[field.key] ?? ''}
            onValueChange={makeOnValueChange(field.key)}
            fullWidth
            readOnly={readOnly}
            onUploadStart={onUploadStart}
            onUploadEnd={onUploadEnd}
          />
        ))}
    </DSBox>
  );
};

export default SectionTemplateDispatcher;
